import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, ref } from "vue";
import useHelpers from "composables/useHelpers.js";
import { useModuleStore } from "store/modules/moduleStore.js";
import { useRoute, useRouter } from "vue-router";
import axios from "axios";
import { useNotificationStore } from "store/modules/notificationStore.js";
import { useAuthenticationStore } from "store/modules/authenticationStore.js";

/**
 * Menu Store
 *
 * This store manages the state and actions related to the menu items.
 */
export const useMenuStore = defineStore("menuItem", () => {
  const { userHasRole, checkRole } = useHelpers();
  const moduleStore = useModuleStore();
  const { hasSettingEnabled } = moduleStore;

  const authenticationStore = useAuthenticationStore();
  const { processResults } = authenticationStore;

  const notificationStore = useNotificationStore();
  const { fetchUnseenNotificationsCount, fetchUnfinishedRequiredTasksCount } =
    notificationStore;

  const router = useRouter();
  const route = useRoute();

  // Refs
  const unreadNotificationsCount = ref(0);
  const menuIsOpen = ref(false);
  const notificationInterval =
    import.meta.env?.VITE_NOTIFICATION_FETCH_INTERVAL ?? 300000;

  // Computed
  /**
   * Determines if the submenu should be hidden.
   * @returns {boolean}
   */
  const hideSubmenu = computed(() => {
    return (
      route?.meta?.hideSubmenu ||
      (subMenuItems.value.length === 1 && subMenuItems.value[0].path === "") ||
      false
    );
  });

  /**
   * Determines if the help menu items can be seen.
   * @returns {boolean}
   */
  const canSeeHelpMenuItems = computed(() => {
    return !checkRole("student");
  });

  // MenuItems Computed
  /**
   * Computes the menu items based on the routes and user roles.
   * @returns {Array<Object>}
   */
  const menuItems = computed(() => {
    const checkRoutes = (routes) => {
      const items = [];

      for (const route of routes) {
        if (
          route.meta?.isMenuItem &&
          userHasRole(route.meta?.roleRequired ?? []) &&
          (!route.meta.settingRequired ||
            route.meta.settingRequired.some(hasSettingEnabled))
        ) {
          let newRoute = { ...route };
          if (route.children) {
            newRoute.children = checkRoutes(route.children);
          }
          items.push(newRoute);
        }
      }

      return items;
    };

    return checkRoutes(router.options.routes).sort(
      (a, b) => a.meta.menuOrder - b.meta.menuOrder,
    );
  });

  /**
   * Computes the help menu items.
   * @returns {Array<Object>}
   */
  const helpItems = computed(() => [
    {
      label: "Helpformulier",
      to: {
        name: "helpForm",
      },
    },
    {
      label: "Trainingen",
      href: "https://catwise.nl/training",
    },
    {
      label: "Kennisbank",
      href: "https://help.catwise.nl/",
    },
    {
      label: "Updates",
      href: "https://help.catwise.nl/article/108-updates",
    },
    {
      label: "Feedback sturen naar Catwise",
      to: {
        name: "feedbackForm",
      },
    },
  ]);

  /**
   * Computes the submenu items for the header.
   * @returns {Array<Object>}
   */
  const subMenuItems = computed(() => {
    const baseRoutes = route?.matched[0]?.children || [];
    const submenuRoutes = [];

    baseRoutes.map((route) => {
      subMenuChecklist(submenuRoutes, route);
    });

    return submenuRoutes;
  });

  /**
   * Computes the tab menu items for the header.
   * @returns {Array<Object>}
   */
  const tabMenuItems = computed(() => {
    const baseRoutes = route?.matched[1]?.children;
    const tabMenuRoutes = [];

    if (!baseRoutes) return [];

    baseRoutes.map((route) => {
      tabMenuChecklist(tabMenuRoutes, route);
    });

    return tabMenuRoutes;
  });

  // Methods
  /**
   * Checks the routes for submenu items.
   * @param {Array<Object>} submenuRoutes - The submenu routes.
   * @param {Object} route - The route to check.
   */
  const subMenuChecklist = (submenuRoutes, route) => {
    if (Array.isArray(route.children) && route.children.length > 0) {
      route.children.forEach((route) => subMenuChecklist(submenuRoutes, route));
    }

    if (
      !route?.meta?.isSubmenuItem ||
      (route?.meta?.settingRequired &&
        !route.meta.settingRequired.some(hasSettingEnabled))
    ) {
      return;
    }

    if (!userHasRole(route.meta?.roleRequired ?? [])) {
      return;
    }

    submenuRoutes.push(route);
  };

  /**
   * Checks the routes for tab menu items.
   * @param {Array<Object>} tabMenuRoutes - The tab menu routes.
   * @param {Object} route - The route to check.
   */
  const tabMenuChecklist = (tabMenuRoutes, route) => {
    if (Array.isArray(route.children) && route.children.length > 0) {
      route.children.forEach((route) => tabMenuChecklist(tabMenuRoutes, route));
    }

    if (!userHasRole(route.meta?.roleRequired ?? [])) {
      return;
    }

    tabMenuRoutes.push(route);
  };

  /**
   * Fetches unread notifications at regular intervals.
   */
  const fetchUnreadNotificationsWithInterval = () => {
    fetchUnseenNotificationsCount();
    setInterval(fetchUnseenNotificationsCount, notificationInterval);
  };

  /**
   * Fetches unfinished tasks at regular intervals.
   */
  const fetchUnfinishedTasksWithInterval = () => {
    // Only students has unfinished tasks
    if (!checkRole("student")) {
      return;
    }

    fetchUnfinishedRequiredTasksCount();
    setInterval(
      () => fetchUnfinishedRequiredTasksCount(),
      notificationInterval,
    );
  };

  /**
   * Checks if a menu item has a dropdown child.
   * @param {Object} item - The menu item to check.
   * @returns {boolean}
   */
  const hasDropdownChild = (item) => {
    if (!item.children?.length) return false;

    item.children.some((child) => {
      if (!child.meta?.settingRequired?.some(hasSettingEnabled)) {
        return false;
      }
    });

    return item.children.some(
      (child) => child?.meta?.inDropdown && showSubItem(child),
    );
  };

  /**
   * Determines if a submenu item should be shown.
   * @param {Object} subItem - The submenu item to check.
   * @returns {boolean}
   */
  const showSubItem = (subItem) => {
    if (!subItem?.meta?.inDropdown) return false;

    if (subItem?.meta?.settingRequired) {
      return subItem?.meta?.settingRequired.some(hasSettingEnabled);
    }

    return true;
  };

  /**
   * Toggles the menu open or closed.
   */
  const toggleMenu = () => {
    menuIsOpen.value = !menuIsOpen.value;
  };

  /**
   * Closes the menu.
   */
  const closeMenu = () => {
    menuIsOpen.value = false;
  };

  /**
   * Logs the user out.
   */
  const logout = () => {
    localStorage.clear();
    window.location.reload();
  };

  /**
   * Logs in as a specified school.
   * @param {Object} school - The school to log in as.
   * @returns {Promise<void>}
   */
  const loginAsSchool = async (school) => {
    localStorage.setItem("school_id", school.id);
    localStorage.setItem("school_name", school.name);
    localStorage.setItem("user_name", school.username);

    let formData = new FormData();
    formData.set("school_id", school.id);

    return await axios.post("/api/login-as-school", formData).then((res) => {
      localStorage.removeItem("school_choices");
      processResults(res.data);
    });
  };

  return {
    menuItems,
    helpItems,
    canSeeHelpMenuItems,
    subMenuItems,
    tabMenuItems,
    hideSubmenu,
    fetchUnreadNotificationsWithInterval,
    fetchUnfinishedTasksWithInterval,
    unreadNotificationsCount,
    toggleMenu,
    closeMenu,
    menuIsOpen,
    logout,
    loginAsSchool,
    hasDropdownChild,
    showSubItem,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useMenuStore, import.meta.hot));
}
